import type { ReactElement } from "react";

import { Children, isValidElement } from "react";
import styled from "styled-components";
import { Box } from "../Box";


type SegmentedButtonProps = {
	children: ReactElement | ReactElement[];
};



/**
 * A component that wraps Button components in a segmented layout
 * @param {SegmentedButtonProps} props - The component props
 * @param {ReactElement | ReactElement[]} props.children - Button components to be wrapped
 * @returns {ReactElement} The segmented button component
 */
export const SegmentedButton = ({ children }: SegmentedButtonProps): ReactElement => {
	// Validate that all children are Button elements
	const validatedChildren = Children.toArray(children).filter((child) => {
		if (!isValidElement(child)) {
			console.warn('SegmentedButton: Non-React element child detected and will be ignored');
			return false;
		}
		
		// Check if the element is a Button component
		const isButton = child.type && (
			(typeof child.type === 'function' && child.type.name === 'Button') ||
			(typeof child.type === 'object' && child.type.displayName === 'Button') ||
			child.props?.label !== undefined // Fallback check for Button-like props
		);
		
		if (!isButton) {
			console.warn('SegmentedButton: Only Button components are allowed as children');
			return false;
		}
		
		return true;
	});

	return (
		<SegmentedButtonContainer>
			{validatedChildren}
		</SegmentedButtonContainer>
	);
};


const SegmentedButtonContainer = styled(Box)`
	flex-direction: row;

	> button {
		border-radius: 0;
	}
	> :first-child {
		border-top-left-radius: 8px;
		border-bottom-left-radius: 8px;
	}
	> :last-child {
		border-top-right-radius: 8px;
		border-bottom-right-radius: 8px;
	}
`;
